import { useState } from 'react';
import { Box, Chip } from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import RefreshIcon from '@mui/icons-material/Refresh';
import AdminTable from '../../shared/AdminTable';
import PaymentIssueDetailModal from './PaymentIssueDetailModal';
import { PaymentIssue, AdminTableColumn, AdminTableAction } from '../../../../types/admin';
import { formatDistanceToNow } from 'date-fns';
import { formatCurrency } from '../../../../utils/formatters';

interface PaymentIssuesTableProps {
  issues: PaymentIssue[];
  loading?: boolean;
  onRetryPayment?: (issueId: string) => Promise<{ success: boolean }>;
}

export default function PaymentIssuesTable({ issues, loading, onRetryPayment }: PaymentIssuesTableProps) {
  const [selectedIssue, setSelectedIssue] = useState<PaymentIssue | null>(null);
  const [detailModalOpen, setDetailModalOpen] = useState(false);

  const columns: AdminTableColumn<PaymentIssue>[] = [
    {
      key: 'user_name',
      label: 'User',
      sortable: true,
      width: 180,
      render: (row) => (
        <Box>
          <Box sx={{ fontWeight: 500 }}>{row.user_name}</Box>
          <Box sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>{row.user_email}</Box>
        </Box>
      ),
    },
    {
      key: 'issue_type',
      label: 'Issue Type',
      sortable: true,
      width: 150,
      render: (row) => {
        const colorMap = {
          failed_payment: 'error' as const,
          expired_card: 'warning' as const,
          declined: 'error' as const,
          chargeback: 'error' as const,
          dispute: 'warning' as const,
        };
        return (
          <Chip
            label={row.issue_type.replace('_', ' ').toUpperCase()}
            size="small"
            color={colorMap[row.issue_type] || 'default'}
          />
        );
      },
    },
    {
      key: 'status',
      label: 'Status',
      sortable: true,
      width: 120,
      render: (row) => {
        const colorMap = {
          new: 'error' as const,
          retrying: 'warning' as const,
          resolved: 'success' as const,
          given_up: 'default' as const,
        };
        return (
          <Chip label={row.status.toUpperCase()} size="small" color={colorMap[row.status] || 'default'} />
        );
      },
    },
    {
      key: 'amount',
      label: 'Amount',
      sortable: true,
      width: 100,
      render: (row) => formatCurrency(row.amount),
    },
    {
      key: 'retry_count',
      label: 'Retries',
      sortable: true,
      width: 100,
      render: (row) => `${row.retry_count} / ${row.max_retries}`,
    },
    {
      key: 'issue_date',
      label: 'Issue Date',
      sortable: true,
      width: 150,
      render: (row) => formatDistanceToNow(new Date(row.issue_date), { addSuffix: true }),
    },
    {
      key: 'error_message',
      label: 'Error',
      sortable: false,
      render: (row) => (
        <Box sx={{ maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {row.error_message}
        </Box>
      ),
    },
  ];

  const actions: AdminTableAction<PaymentIssue>[] = [
    {
      label: 'View Details',
      icon: <VisibilityIcon />,
      onClick: (row) => {
        setSelectedIssue(row);
        setDetailModalOpen(true);
      },
      color: 'primary',
    },
    {
      label: 'Retry Payment',
      icon: <RefreshIcon />,
      onClick: async (row) => {
        if (onRetryPayment) {
          await onRetryPayment(row.id);
        }
      },
      color: 'warning',
      disabled: (row) => row.status === 'resolved' || row.status === 'given_up',
    },
  ];

  return (
    <>
      <AdminTable columns={columns} data={issues} actions={actions} isLoading={loading} />

      {selectedIssue && (
        <PaymentIssueDetailModal
          open={detailModalOpen}
          issue={selectedIssue}
          onClose={() => setDetailModalOpen(false)}
          onRetryPayment={onRetryPayment}
        />
      )}
    </>
  );
}
